// [목적] 가이드 '출발 날짜' 전용 월 캘린더 — 브랜드 토큰으로 직접 그린 날짜 선택 팝오버
// [이유] [2026-09-21] 네이티브 <input type="date"> 의 showPicker() 는 OS/브라우저 기본 달력을 띄운다.
//        파란 시스템 하이라이트, '삭제/오늘' 버튼, 시스템 폰트가 그대로 나와 앱 톤과 완전히 어긋났다
//        (사용자 피드백: "저거 너무 별로야"). 캘린더를 직접 그려 같은 팔레트·모서리·타이포로 맞춘다.
// [사이드 이팩트] 없음(순수 표시 + onSelect 콜백). 날짜 계산은 전부 로컬 Date 기준.
// [수정 시 고려사항] 'YYYY-MM-DD' 문자열을 new Date() 에 그대로 넣으면 UTC 로 해석돼 KST 에서 하루
//                   밀린다 — 이 파일은 항상 연/월/일을 분해해 로컬 Date 를 만든다(parseIso 참조).
//                   색은 packages/ui 토큰만 사용(CLAUDE.md §6-3). 토/일 색 구분은 한국 달력 관행.

import { useState } from 'react';
import { Box, ButtonBase, Popover, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { tokens } from '@gangwoncanvas/ui';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const PAPER0 = c.paper[0][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];
const BRAND_TEXT = c.brand[600][L];
const DANGER = c.danger[500][L];
const MONO = 'JetBrains Mono, ui-monospace, monospace';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'] as const;

/** 'YYYY-MM-DD' → 로컬 Date (UTC 해석으로 하루 밀리는 것 방지) */
function parseIso(iso: string | undefined): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function atMidnight(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * [목적] 특정 달의 그리드(6주 × 7일)에 들어갈 날짜 배열 생성
 * [이유] 앞뒤 달 날짜를 흐리게 채워야 한 달이 항상 같은 높이로 보인다(달마다 높이가 튀면 팝오버가 흔들린다).
 * [사이드 이팩트] 없음(순수 함수).
 */
function buildMonthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());
  return Array.from(
    { length: 42 },
    (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i),
  );
}

export interface GuideCalendarProps {
  /** 현재 선택값 'YYYY-MM-DD'. 없으면 undefined */
  value?: string;
  /** 선택 가능한 최소 날짜 'YYYY-MM-DD' (보통 오늘) */
  min?: string;
  /** 팝오버를 붙일 기준 엘리먼트. null 이면 닫힘 */
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSelect: (iso: string) => void;
}

/**
 * [목적] 날짜 선택 팝오버 본체
 * [이유] 파일 헤더 참조 — 네이티브 달력 대체.
 * [사이드 이팩트] 없음. 날짜를 고르면 onSelect 후 onClose 까지 호출한다(고르면 닫히는 게 자연스럽다).
 * [수정 시 고려사항] 월 이동은 min 이전 달로는 가지 않는다 — 갈 수 없는 달을 보여줄 이유가 없다.
 */
export function GuideCalendar({
  value,
  min,
  anchorEl,
  onClose,
  onSelect,
}: GuideCalendarProps): JSX.Element {
  const today = atMidnight(new Date());
  const minDate = parseIso(min) ?? today;
  const selected = parseIso(value);
  const base = selected ?? minDate;
  const [cursor, setCursor] = useState(() => new Date(base.getFullYear(), base.getMonth(), 1));

  const grid = buildMonthGrid(cursor.getFullYear(), cursor.getMonth());
  // min 이 속한 달보다 이전으로는 이동 불가
  const canGoPrev =
    cursor.getFullYear() > minDate.getFullYear() ||
    (cursor.getFullYear() === minDate.getFullYear() && cursor.getMonth() > minDate.getMonth());

  const shiftMonth = (delta: number) =>
    setCursor((cur) => new Date(cur.getFullYear(), cur.getMonth() + delta, 1));

  const navBtn = {
    width: 28,
    height: 28,
    borderRadius: `${tokens.radius.s}px`,
    border: `1px solid ${BORDER}`,
    bgcolor: PAPER0,
    fontSize: 13,
    fontWeight: 800,
    color: INK_MID,
    '&:disabled': { opacity: 0.35 },
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            mt: '8px',
            p: '14px',
            width: 280,
            borderRadius: `${tokens.radius.l}px`,
            border: `1px solid ${BORDER}`,
            boxShadow: tokens.shadow.card,
            bgcolor: PAPER0,
          },
        },
      }}
    >
      {/* 헤더 — 연월 + 이전/다음 달 */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: '10px' }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: INK_HI, fontFamily: MONO }}>
          {cursor.getFullYear()}.{String(cursor.getMonth() + 1).padStart(2, '0')}
        </Typography>
        <Stack direction="row" spacing="6px">
          <ButtonBase
            onClick={() => shiftMonth(-1)}
            disabled={!canGoPrev}
            aria-label="이전 달"
            sx={navBtn}
          >
            ‹
          </ButtonBase>
          <ButtonBase onClick={() => shiftMonth(1)} aria-label="다음 달" sx={navBtn}>
            ›
          </ButtonBase>
        </Stack>
      </Stack>

      {/* 요일 헤더 — 토/일 색 구분(한국 달력 관행) */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: '4px' }}>
        {WEEKDAYS.map((w, i) => (
          <Typography
            key={w}
            sx={{
              textAlign: 'center',
              fontSize: 10.5,
              fontWeight: 800,
              color: i === 0 ? DANGER : i === 6 ? BRAND_TEXT : INK_LOW,
            }}
          >
            {w}
          </Typography>
        ))}
      </Box>

      {/* 날짜 그리드 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {grid.map((d) => {
          const iso = toIso(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const disabled = d.getTime() < minDate.getTime();
          const isSelected = selected !== null && iso === toIso(selected);
          const isToday = iso === toIso(today);
          const weekday = d.getDay();

          return (
            <ButtonBase
              key={iso}
              disabled={disabled}
              onClick={() => {
                onSelect(iso);
                onClose();
              }}
              aria-label={`${d.getMonth() + 1}월 ${d.getDate()}일`}
              aria-pressed={isSelected}
              sx={{
                height: 34,
                borderRadius: `${tokens.radius.s}px`,
                fontSize: 12.5,
                fontWeight: isSelected || isToday ? 800 : 600,
                bgcolor: isSelected ? BRAND : isToday ? alpha(BRAND, 0.08) : 'transparent',
                color: isSelected
                  ? PAPER0
                  : !inMonth || disabled
                    ? INK_LOW
                    : weekday === 0
                      ? DANGER
                      : weekday === 6
                        ? BRAND_TEXT
                        : INK_HI,
                // 이번 달이 아닌 날짜는 존재만 알리고 시선을 뺏지 않는다
                opacity: disabled ? 0.3 : inMonth ? 1 : 0.4,
                '&:hover': { bgcolor: isSelected ? BRAND : PAPER2 },
              }}
            >
              {d.getDate()}
            </ButtonBase>
          );
        })}
      </Box>

      {/* 푸터 — 오늘로 점프 */}
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: '10px' }}>
        <ButtonBase
          onClick={() => {
            onSelect(toIso(today));
            onClose();
          }}
          sx={{
            px: '12px',
            minHeight: 32,
            borderRadius: 999,
            border: `1px solid ${BORDER}`,
            fontSize: 11.5,
            fontWeight: 700,
            color: BRAND_TEXT,
          }}
        >
          오늘로
        </ButtonBase>
      </Stack>
    </Popover>
  );
}
